/**
 * Cadastro de produtos — lista (`/produtos`).
 *
 * `products` é cadastro NOSSO desde 2026-09-25: o Bling só vincula `bling_id`,
 * importa SKU inexistente e traz saldo (NF série 1 e kits continuam lá). Aqui
 * se busca, filtra e abre o editor (`/produtos/<id>`, sempre pelo UUID).
 *
 * Carga: o catálogo inteiro (páginas de 1000, hoje ~790 produtos) e o catálogo
 * de pendências (`GET /products/pending`: rótulos e contagens) em paralelo.
 * Busca e filtros rodam no cliente e ficam NA URL (`?q=`, `?situacao=`,
 * `?pendencia=`, `?origem=`, escritos com `replace`), para o "← Produtos" do
 * editor voltar ao mesmo recorte.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Plus } from 'shared/icons';
import PageHeader from '../../components/layout/PageHeader';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import ErrorCard from '../../components/ui/ErrorCard';
import FilterChip from '../../components/ui/FilterChip';
import { Id, MONO_CLASS, Sku } from '../../components/ui/Mono';
import Select from '../../components/ui/Select';
import { SkeletonTable } from '../../components/ui/Skeleton';
import StandardTable from '../../components/ui/StandardTable';
import { PRODUCT_ORIGIN_LABELS, PRODUCT_STATUS_META } from '../../constants/productCatalog';
import { useAuth } from '../../hooks/useAuth';
import api from '../../services/api';
import { FormatInt, FormatMoney } from '../../utils/format';
import { OnlyDigits } from '../../utils/productCodes';
import { BlingLinkBadge, ProductStatusDot } from './editorParts';

const PAGE_LIMIT = 1000;
// Teto de segurança da carga paginada (10 mil produtos).
const MAX_PAGES = 10;

const STATUS_CHIPS = [
    { id: 'todos', label: 'Todos', match: () => true },
    { id: 'ativos', label: 'Ativos', match: (row) => row.status !== 'INACTIVE' },
    { id: 'inativos', label: 'Inativos', match: (row) => row.status === 'INACTIVE' },
];

async function loadAllProducts() {
    const rows = [];
    let total = Number.POSITIVE_INFINITY;
    for (let page = 0; page < MAX_PAGES && rows.length < total; page += 1) {
        const data = await api.getCatalogProducts({
            limit: PAGE_LIMIT,
            offset: page * PAGE_LIMIT,
            sort_by: 'sku',
            sort_direction: 'asc',
        });
        const results = Array.isArray(data?.results) ? data.results : [];
        rows.push(...results);
        const pagingTotal = Number(data?.paging?.total);
        total = Number.isFinite(pagingTotal) ? pagingTotal : rows.length;
        if (results.length < PAGE_LIMIT) {
            break;
        }
    }
    return rows;
}

/** Busca no cliente: SKU, nome, GTIN (os três códigos), marca e código do fornecedor. */
function matchesSearch(row, term) {
    if (!term) {
        return true;
    }
    const lower = term.toLowerCase();
    const digits = OnlyDigits(term);
    const texts = [row.sku, row.sku_display, row.title, row.brand, row.supplier_code];
    if (
        texts.some((value) =>
            String(value ?? '')
                .toLowerCase()
                .includes(lower),
        )
    ) {
        return true;
    }
    if (digits && digits.length === term.trim().length) {
        return [row.ean, row.tax_ean, row.case_gtin].some((code) => String(code ?? '').includes(digits));
    }
    return false;
}

const ProductsPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { checkPermission } = useAuth();
    const canEdit = checkPermission('stock:write');

    const [rows, setRows] = useState([]);
    const [pendingCatalog, setPendingCatalog] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);

    const search = searchParams.get('q') || '';
    const statusFilter = STATUS_CHIPS.some((chip) => chip.id === searchParams.get('situacao'))
        ? searchParams.get('situacao')
        : 'todos';
    const pendingFilter = searchParams.get('pendencia') || '';
    const originFilter = searchParams.get('origem') || '';

    const setParam = useCallback(
        (key, value, fallback = '') => {
            setSearchParams(
                (current) => {
                    const next = new URLSearchParams(current);
                    if (!value || value === fallback) {
                        next.delete(key);
                    } else {
                        next.set(key, value);
                    }
                    return next;
                },
                { replace: true },
            );
        },
        [setSearchParams],
    );

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const [products, pending] = await Promise.all([
                loadAllProducts(),
                api.getCatalogPending().catch(() => null),
            ]);
            setRows(products);
            setPendingCatalog(Array.isArray(pending?.codes) ? pending.codes : []);
        } catch (error) {
            setLoadError(error?.message || 'Erro ao carregar');
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const pendingLabels = useMemo(
        () => Object.fromEntries(pendingCatalog.map((entry) => [entry.code, entry.label])),
        [pendingCatalog],
    );

    // Filtros que NÃO são a situação: a contagem dos chips respeita o resto do recorte.
    const baseRows = useMemo(
        () =>
            rows.filter(
                (row) =>
                    matchesSearch(row, search.trim()) &&
                    (!pendingFilter || (row.pending_codes || []).includes(pendingFilter)) &&
                    (!originFilter || row.origin === originFilter),
            ),
        [rows, search, pendingFilter, originFilter],
    );
    const chipCounts = useMemo(
        () => Object.fromEntries(STATUS_CHIPS.map((chip) => [chip.id, baseRows.filter(chip.match).length])),
        [baseRows],
    );
    const visibleRows = useMemo(() => {
        const chip = STATUS_CHIPS.find((item) => item.id === statusFilter) || STATUS_CHIPS[0];
        return baseRows.filter(chip.match);
    }, [baseRows, statusFilter]);

    const openProduct = useCallback(
        (row) => {
            if (!row?.id) {
                return;
            }
            navigate(`/produtos/${row.id}`, { state: { from: `${location.pathname}${location.search}` } });
        },
        [navigate, location.pathname, location.search],
    );

    const columns = useMemo(
        () => [
            {
                key: 'title',
                label: 'Produto',
                getValue: (row) => row.title || '',
                render: (row) => (
                    <div className="flex min-w-[16rem] max-w-[28rem] flex-col gap-0.5">
                        <span className="truncate text-black dark:text-white" title={row.title || undefined}>
                            {row.title || <span className="text-mono-400">—</span>}
                        </span>
                        {row.format === 'KIT' ? (
                            <span>
                                <Badge variant="info">Kit</Badge>
                            </span>
                        ) : null}
                    </div>
                ),
            },
            {
                key: 'brand',
                label: 'Marca',
                getValue: (row) => row.brand || '',
                render: (row) => row.brand || <span className="text-mono-400">sem marca</span>,
            },
            {
                key: 'sku',
                label: 'SKU',
                getValue: (row) => row.sku_display || row.sku || '',
                render: (row) => <Sku value={row.sku_display || row.sku} />,
            },
            {
                key: 'ean',
                label: 'GTIN',
                getValue: (row) => row.ean || '',
                render: (row) => <Id value={row.ean} blank="sem GTIN" />,
            },
            {
                key: 'sale_price',
                label: 'Preço',
                align: 'right',
                getValue: (row) =>
                    row.sale_price === null || row.sale_price === undefined ? '' : Number(row.sale_price),
                render: (row) => <span className={MONO_CLASS}>{FormatMoney(row.sale_price)}</span>,
            },
            {
                key: 'cost_price',
                label: 'Custo',
                align: 'right',
                getValue: (row) =>
                    row.cost_price === null || row.cost_price === undefined ? '' : Number(row.cost_price),
                render: (row) =>
                    row.cost_price === null || row.cost_price === undefined || Number(row.cost_price) === 0 ? (
                        <Badge variant="warning">pendente</Badge>
                    ) : (
                        <span className={MONO_CLASS}>{FormatMoney(row.cost_price)}</span>
                    ),
            },
            {
                key: 'status',
                label: 'Situação',
                getValue: (row) => PRODUCT_STATUS_META[row.status]?.label || '',
                render: (row) => <ProductStatusDot status={row.status} />,
            },
            {
                key: 'origin',
                label: 'Origem',
                getValue: (row) => PRODUCT_ORIGIN_LABELS[row.origin] || '',
                render: (row) => (
                    <span className="whitespace-nowrap text-mono-600 dark:text-mono-400">
                        {PRODUCT_ORIGIN_LABELS[row.origin] || '—'}
                    </span>
                ),
            },
            {
                key: 'bling_status',
                label: 'Bling',
                getValue: (row) => row.bling_status || '',
                render: (row) => <BlingLinkBadge status={row.bling_status} blingId={row.bling_id} compact />,
            },
            {
                key: 'pending_codes',
                label: 'Pendências',
                align: 'right',
                getValue: (row) => (Array.isArray(row.pending_codes) ? row.pending_codes.length : 0),
                render: (row) => {
                    const codes = Array.isArray(row.pending_codes) ? row.pending_codes : [];
                    if (codes.length === 0) {
                        return <span className="text-mono-400">—</span>;
                    }
                    const title = codes.map((code) => pendingLabels[code] || code).join('\n');
                    return (
                        <span title={title} className={`${MONO_CLASS} text-warning-700 dark:text-warning-300`}>
                            {FormatInt(codes.length)}
                        </span>
                    );
                },
            },
        ],
        [pendingLabels],
    );

    const toolbarLeft = (
        <div className="flex flex-wrap items-center gap-2">
            {STATUS_CHIPS.map((chip) => (
                <FilterChip
                    key={chip.id}
                    active={statusFilter === chip.id}
                    count={chipCounts[chip.id] ?? 0}
                    onClick={() => setParam('situacao', chip.id, 'todos')}
                >
                    {chip.label}
                </FilterChip>
            ))}
            <div className="w-56">
                <Select
                    aria-label="Pendência"
                    value={pendingFilter}
                    onChange={(event) => setParam('pendencia', event.target.value)}
                >
                    <option value="">Todas as pendências</option>
                    {pendingCatalog.map((entry) => (
                        <option key={entry.code} value={entry.code}>
                            {`${entry.label} (${FormatInt(entry.count)})`}
                        </option>
                    ))}
                </Select>
            </div>
            <div className="w-48">
                <Select
                    aria-label="Origem"
                    value={originFilter}
                    onChange={(event) => setParam('origem', event.target.value)}
                >
                    <option value="">Todas as origens</option>
                    {Object.entries(PRODUCT_ORIGIN_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                            {label}
                        </option>
                    ))}
                </Select>
            </div>
        </div>
    );

    return (
        <div className="space-y-6">
            <PageHeader
                title="Produtos"
                subtitle="Cadastro de produtos. O Bling continua cuidando do saldo vendável, da nota fiscal e da composição dos kits."
                actions={
                    canEdit ? (
                        <Button
                            onClick={() =>
                                navigate('/produtos/novo', {
                                    state: { from: `${location.pathname}${location.search}` },
                                })
                            }
                        >
                            <Plus size={18} className="mr-2" />
                            Novo produto
                        </Button>
                    ) : null
                }
            />

            {loading ? (
                <SkeletonTable columns={9} rows={10} />
            ) : loadError ? (
                <ErrorCard
                    title="Produtos"
                    message={`Não foi possível carregar os dados.\n${loadError}`}
                    action="Tentar novamente"
                    actionFn={load}
                />
            ) : (
                <StandardTable
                    rows={visibleRows}
                    columns={columns}
                    rowKey={(row, index) => row.id || row.sku || index}
                    countLabel="produtos"
                    exportFileName="produtos"
                    emptyMessage={rows.length === 0 ? 'Nenhum produto cadastrado.' : 'Nenhum produto neste filtro.'}
                    defaultPageSize={50}
                    pageSizeOptions={[50, 100, 200]}
                    searchMode="server"
                    searchTerm={search}
                    onSearchTermChange={(value) => setParam('q', value)}
                    searchPlaceholder="Buscar por nome, SKU, GTIN ou marca"
                    toolbarLeft={toolbarLeft}
                    onRowClick={openProduct}
                />
            )}
        </div>
    );
};

export default ProductsPage;
